<template>
  <div class="app-container">
    <Container
      :title="t('approvalForm.approvalFormHistoryList')"
      :hasLine="true"
    >
      <template #header>
        <SearchForm
          :formModel="searchFormModel"
          :formStruct="searchFormStruct"
          :hasCreate="false /*$hasPermission(['approvalform_M'])*/"
          :submitButton="t('common.search')"
          :resetButton="t('common.reset')"
          @submit="submitSearch"
          @reset="resetForm"
        />
      </template>
      <template #main>
        <SimpleTable
          :listLoading="listLoading"
          :tableData="tableData"
          :columnStruct="columnStruct"
          :operateColumnWidth="/*$hasPermission(['approvalform_M'])*/ true ? 220:80"
        >
          <template
            #default="slotProps"
          >
            <el-button
              v-if="/*$hasPermission(['approvalform_M'])*/ true"
              @click="handleExamine(slotProps.row)"
              type="primary"
              size="mini"
            >
              {{ t('common.examine') }}
            </el-button>
          </template>
        </SimpleTable>
        <el-pagination
          :total="total"
          v-show="total > 0"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :page-size="limit"
          :current-page="page"
          :page-sizes="[10, 20, 50, 100, 200]"
          layout="total, sizes, prev, pager, next, jumper"
          style="text-align: right;"
        />
      </template>
    </Container>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, toRefs, onMounted, onActivated } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatDate, toMaximumTimeOfDay } from '@/utils'
import { createModel } from '@/utils/component'
import Container from '@/components/form/container/Container.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import SearchForm from '@/components/form/search/SearchForm.vue'
import { ApprovalFormModel, ApprovalFormHistoryPageParamModel } from '@/model/process/approvalModel'
import { useRouter } from 'vue-router'
import { getApprovalFormHistoryPageList } from '@/apis/process/approval'
import dayjs from 'dayjs'
import { CompanyListParamModel } from '@/model/master-data/companyModel'
import { getCompanyAllList } from '@/apis/main-data/company'

export default defineComponent({
  name: 'ApprovalFormHistoryPageList',
  components: {
    SearchForm,
    SimpleTable,
    Container
  },
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const searchStruct = [
      {
        prop: 'companyCode',
        label: t('company.companyCode'),
        component: 'el-select',
        type: 'text',
        model: '',
        filterable: true,
        loading: false,
        options: [] as any
      },
      {
        prop: 'status',
        label: t('table.status'),
        component: 'el-select',
        options: [
          { label: t('common.hasApproved'), value: 4 },
          { label: t('common.partiallyPassed'), value: 5 },
          { label: t('common.rejected'), value: 8 },
          { label: t('common.canceled'), value: 9 }
        ]
      },
      // { prop: 'updatedAtStart', label: t('approvalform.operateDate'), component: 'el-input', type: 'date', model: '' },
      // { prop: 'updatedAtEnd', label: t('approvalform.operateDate'), component: 'el-input', type: 'date', model: '' },
      { prop: 'dateRange', label: t('approvalForm.operateDate'), component: 'el-date-picker', type: 'daterange', model: [] },
      { prop: 'approver', label: t('approvalForm.finalApprover'), component: 'el-input', type: 'text', model: '' }
    ]
    const formatStatus = (row: ApprovalFormModel) => {
      if (row.status === 4 && (row.notApprovedCount == null || row.notApprovedCount === '0')) {
        return t('common.hasApproved')
      } if (row.status === 4) {
        return t('common.partiallyPassed')
      } else if (row.status === 8) {
        return t('common.rejected')
      } else if (row.status === 9) {
        return t('common.canceled')
      }
      return null
    }
    const approvalFormTableStruct = [
      { prop: 'id', label: t('approvalForm.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'userName', label: t('approvalForm.submitter') },
      { prop: 'submitDate', label: t('approvalForm.submitDate'), formatter: (row: any) => formatDate(row.submitDate, 'yyyy-MM-dd') },
      { prop: 'approver', label: t('approvalForm.finalApprover') },
      { prop: 'updatedAt', label: t('approvalForm.operateDate'), formatter: (row: any) => formatDate(row.updatedAt, 'yyyy-MM-dd') },
      { prop: 'status', label: t('table.status'), formatter: (row: any) => formatStatus(row) }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as any,
      tableData: Array<ApprovalFormModel>(),
      columnStruct: approvalFormTableStruct,
      listLoading: true,
      limit: 10,
      total: 0,
      page: 1,
      handleCurrentChange(page?: any) {
        dataMap.getPageList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getPageList(null, val)
      },
      async getPageList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }
        dataMap.listLoading = true

        let startDate = null as string | null
        let endDate = null as string | null
        if (dataMap.searchFormModel.dateRange && dataMap.searchFormModel.dateRange.length > 1) {
          startDate = dataMap.searchFormModel.dateRange[0] ? dayjs(dataMap.searchFormModel.dateRange[0]).format('YYYY-MM-DD HH:mm:ss') : null
          endDate = dataMap.searchFormModel.dateRange[1] ? dayjs(toMaximumTimeOfDay(dataMap.searchFormModel.dateRange[1])).format('YYYY-MM-DD HH:mm:ss') : null
        }

        const params: ApprovalFormHistoryPageParamModel = {
          companyCode: dataMap.searchFormModel.companyCode,
          status: dataMap.searchFormModel.status,
          updatedAtStart: startDate,
          updatedAtEnd: endDate,
          approver: dataMap.searchFormModel.approver,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        const data = await getApprovalFormHistoryPageList(params)
        if (data && data.data && data.data.list.length > 0) {
          dataMap.tableData = data.data.list
        } else {
          dataMap.tableData = []
        }
        dataMap.total = data?.data.totalCount ?? 0

        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getPageList()
      },
      resetForm() {
        console.log('reset')
      }
    })
    const handleExamine = (row: ApprovalFormModel) => {
      router.push({ path: `history/approvalFormDetail/${row.id}` })
    }
    const getCompanyList = async() => {
      dataMap.searchFormStruct[0].loading = true
      const params: CompanyListParamModel = {
        name: null,
        companyCode: null
      }
      const data = await getCompanyAllList(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({
          label: iterator.companyCode + ', ' + iterator.name,
          value: iterator.companyCode
        })
      }
      dataMap.searchFormStruct[0].options = optionList
      dataMap.searchFormStruct[0].loading = false
    }
    onMounted(() => {
      dataMap.getPageList(null, 10)
      getCompanyList()
    })
    onActivated(async() => {
      if (history.state?.refresh === true) {
        await dataMap.getPageList()
        // 用完删除
        const state = { ...history.state }
        delete state.refresh
        history.replaceState(state, '')
      }
    })
    return {
      t,
      handleExamine,
      ...toRefs(dataMap)
    }
  }
})
</script>

<style
  scoped
  lang="scss"
>

</style>
